import { useRef, useState } from "react";
import Cropper from "react-easy-crop";
import { Upload, X, Check } from "lucide-react";
import { motion } from "framer-motion";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

async function cropToBlob(src, area) {
  const img = await new Promise((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = src;
  });
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(area.width);
  canvas.height = Math.round(area.height);
  canvas.getContext("2d").drawImage(img, area.x, area.y, area.width, area.height, 0, 0, area.width, area.height);
  return new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.9));
}

export default function ImageUploader({ onUploaded, label = "Upload Image", aspect = 4 / 5, testid = "image-uploader" }) {
  const [src, setSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [area, setArea] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const fileMeta = useRef(null);

  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) return toast.error("Image must be under 10 MB");
    fileMeta.current = f;
    setSrc(URL.createObjectURL(f));
    e.target.value = "";
  };

  const upload = async (blob, filename) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", blob, filename);
      const { data } = await api.post("/admin/upload", fd);
      toast.success("Image uploaded");
      onUploaded(data.url);
      setSrc(null);
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setUploading(false);
  };

  return (
    <>
      <button type="button" onClick={() => fileRef.current?.click()} data-testid={testid}
        className="inline-flex items-center gap-2 text-xs font-semibold tracking-wider uppercase border border-[#7E1E3F]/50 text-[#5C1230] rounded-full px-4 py-2 hover:bg-[#7E1E3F]/10 transition-colors">
        <Upload size={13} /> {label}
      </button>
      <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={pick} data-testid={`${testid}-input`} />
      {src && (
        <div className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="crop-modal">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-2xl w-full max-w-lg overflow-hidden shadow-2xl">
            <div className="flex items-center justify-between px-5 py-3 border-b border-[#E5E7EB]">
              <p className="font-serif-lux text-lg text-[#2E1A22]">Crop & Upload</p>
              <button onClick={() => setSrc(null)} className="text-[#857078]" data-testid="crop-cancel-x"><X size={18} /></button>
            </div>
            <div className="relative h-80 bg-[#FBF7F1]">
              <Cropper image={src} crop={crop} zoom={zoom} aspect={aspect}
                onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={(_, a) => setArea(a)} />
            </div>
            <div className="px-5 py-3">
              <input type="range" min={1} max={3} step={0.05} value={zoom} onChange={(e) => setZoom(+e.target.value)} className="w-full accent-[#7E1E3F]" data-testid="crop-zoom" />
            </div>
            <div className="flex gap-3 px-5 pb-5">
              <button disabled={uploading} onClick={async () => upload(await cropToBlob(src, area), "cropped.jpg")}
                className="flex-1 inline-flex justify-center items-center gap-2 bg-[#7E1E3F] text-white text-xs font-semibold tracking-wider uppercase rounded-full py-3 hover:bg-[#5C1230] transition-colors disabled:opacity-60"
                data-testid="crop-upload-btn"><Check size={14} /> {uploading ? "Uploading…" : "Upload Cropped"}</button>
              <button disabled={uploading} onClick={() => upload(fileMeta.current, fileMeta.current.name)}
                className="flex-1 text-xs font-semibold tracking-wider uppercase border border-[#E5E7EB] text-[#45303A] rounded-full py-3 hover:border-[#7E1E3F] transition-colors"
                data-testid="crop-original-btn">Use Original</button>
            </div>
          </motion.div>
        </div>
      )}
    </>
  );
}
